import React from 'react'
import { CORE_TOOLS, OPS_DASHBOARDS } from './mockData'
import { ICON_MAP } from './icons'

function ModuleCard({ module, onClick }) {
  const IconComponent = ICON_MAP[module.icon]
  const Tag = onClick ? 'div' : 'a'
  const props = onClick
    ? { className: 'module-card', onClick, style: { cursor: 'pointer' } }
    : { className: 'module-card', href: module.href }
  return (
    <Tag {...props}>
      <div className="card-stripe" style={{ background: module.stripeColor }} />
      <div className="card-head">
        <div className="card-icon" style={{ background: module.iconBg, color: module.iconColor }}>
          {IconComponent && <IconComponent size={16} />}
        </div>
        <div className="card-title">{module.title}</div>
        {module.pill && (
          <span className={`pill pill-${module.pill.tone}`} style={{ fontSize: 9, marginLeft: 'auto' }}>
            <span className="dot" />{module.pill.label}
          </span>
        )}
      </div>
      <div className="card-desc">{module.desc}</div>
      <div className="card-stats">
        {module.stats.map((s, i) => (
          <span className="card-stat" key={i}><b>{s.value}</b> {s.label}</span>
        ))}
      </div>
    </Tag>
  )
}

function patchCard(card, stats) {
  if (!stats) return card

  switch (card.id) {
    case 'bids': {
      const s = stats.bids
      if (!s) return card
      return {
        ...card,
        pill: s.dueThisWeek > 0 ? { label: `${s.dueThisWeek} due`, tone: 'warn' } : null,
        stats: [
          { value: String(s.active), label: 'active' },
          { value: String(s.drafts), label: 'draft' },
          { value: s.totalValue || '$0', label: 'total' }
        ]
      }
    }
    case 'projects': {
      const s = stats.projects
      if (!s) return card
      return {
        ...card,
        pill: s.active > 0 ? { label: 'Active', tone: 'ok' } : null,
        stats: [
          { value: String(s.active), label: 'active' },
          { value: String(s.inProgress), label: 'in progress' }
        ]
      }
    }
    case 'leads': {
      const s = stats.leads
      if (!s) return card
      return {
        ...card,
        pill: s.new > 0 ? { label: `${s.new} new`, tone: 'info' } : null,
        stats: [
          { value: String(s.total), label: 'total' },
          { value: String(s.qualified), label: 'qualified' },
          { value: `${s.convRate}%`, label: 'conv.' }
        ]
      }
    }
    case 'herbicide': {
      const s = stats.herbicide
      if (!s) return card
      return {
        ...card,
        pill: s.overdue > 0 ? { label: `${s.overdue} overdue`, tone: 'risk' } : null,
        stats: [
          { value: String(s.scheduled), label: 'scheduled' },
          { value: `${s.compliancePct}%`, label: 'compliant' }
        ]
      }
    }
    case 'irrigation': {
      const s = stats.irrigation
      if (!s) return card
      return {
        ...card,
        pill: s.offline > 0 ? { label: `${s.offline} offline`, tone: 'warn' } : null,
        stats: [
          { value: String(s.zones), label: 'zones' },
          { value: String(s.offline), label: 'offline' }
        ]
      }
    }
    case 'equipment': {
      const s = stats.equipment
      if (!s) return card
      const expired = s.registrations?.expired || 0
      return {
        ...card,
        pill: expired > 0 ? { label: `${expired} expired`, tone: 'risk' } : null,
        stats: [
          { value: String(s.totalActive), label: 'in fleet' },
          { value: String(expired), label: 'expired reg' },
          { value: String(s.registrations?.expiringSoon || 0), label: 'expiring' }
        ]
      }
    }
    case 'shop': {
      const s = stats.shop
      if (!s) return card
      const faults = s.faultCodes?.total || 0
      return {
        ...card,
        pill: faults > 0 ? { label: `${faults} fault${faults !== 1 ? 's' : ''}`, tone: 'risk' } : null,
        stats: [
          { value: String(s.repairs?.open || 0), label: 'open repairs' },
          { value: String(s.repairRequests?.open || 0), label: 'requests' },
          { value: String(faults), label: 'faults' }
        ]
      }
    }
    case 'disposal': {
      const s = stats.disposal
      if (!s) return card
      return {
        ...card,
        stats: [
          { value: String(s.facilities), label: 'facilities' },
          { value: String(s.vendorRates), label: 'vendor rates' }
        ]
      }
    }
    case 'aggregate': {
      const s = stats.aggregate
      if (!s) return card
      return {
        ...card,
        stats: [
          { value: String(s.materials), label: 'materials' },
          { value: String(s.offerings), label: 'offerings' },
          { value: String(s.inStock), label: 'in stock' }
        ]
      }
    }
    default:
      return card
  }
}

export default function ModuleCards({ moduleStats, shopStats, equipmentStats }) {
  // Merge dedicated shop/equipment stats into moduleStats shape
  const stats = { ...(moduleStats || {}) }
  if (equipmentStats) stats.equipment = equipmentStats
  if (shopStats) stats.shop = shopStats

  return (
    <>
      <div>
        <div className="section-head">
          <h3>Core Tools</h3>
          <span className="pill pill-neutral" style={{ fontSize: '9.5px' }}>{CORE_TOOLS.length} modules</span>
        </div>
        <div className="cards-grid">
          {CORE_TOOLS.map((m) => <ModuleCard key={m.id} module={patchCard(m, stats)} />)}
        </div>
      </div>

      <div>
        <div className="section-head">
          <h3>Operations Dashboards</h3>
          <span className="pill pill-neutral" style={{ fontSize: '9.5px' }}>{OPS_DASHBOARDS.length} dashboards</span>
        </div>
        <div className="cards-grid">
          {OPS_DASHBOARDS.map((m) => <ModuleCard key={m.id} module={patchCard(m, stats)} />)}
        </div>
      </div>
    </>
  )
}
